import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  MoreHorizontal, 
  UserPlus, 
  Users,
  TrendingUp,
  Clock,
  ChevronRight,
  ShoppingBag
} from 'lucide-react';
import { feedService } from '../services/followService';
import { getAssetUrl } from '../config/config';

const FeedContainer = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  color: #ffffff;
  padding: 20px;
`;

const FeedLayout = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const MainFeed = styled.div`
  background: #111111;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #333333;
`;

const FeedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #333333;
`;

const FeedTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const FeedTabs = styled.div`
  display: flex;
  gap: 8px;
`;

const TabButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : 'transparent'};
  color: ${props => props.active ? '#000000' : '#cccccc'};
  border: 1px solid ${props => props.active ? 'transparent' : '#333333'};
  padding: 8px 16px;
  border-radius: 8px;
  font-family: var(--font-heading);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : '#1a1a1a'};
  }
`;

const PostCard = styled(motion.div)`
  background: #1a1a1a;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #444444;
    transform: translateY(-2px);
  }
`;

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CreatorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CreatorAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading);
  font-weight: 600;
  color: #000000;
  font-size: 0.875rem;
`;

const CreatorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const CreatorName = styled.span`
  font-family: var(--font-heading);
  font-weight: 600;
  color: #ffffff;
  font-size: 0.875rem;
`;

const PostTime = styled.span`
  font-family: var(--font-body);
  color: #666666;
  font-size: 0.75rem;
`;

const PostMenu = styled.button`
  background: transparent;
  border: none;
  color: #666666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ffffff;
    background: #333333;
  }
`;

const PostContent = styled.div`
  margin-bottom: 16px;
`;

const PostTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 8px 0;
`;

const PostText = styled.p`
  font-family: var(--font-body);
  color: #cccccc;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 12px 0;
`;

const PostImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #111111 0%, #1a1a1a 100%);
  border: 1px solid #333333;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-gold);
  font-size: 0.875rem;
  margin-bottom: 12px;
`;

const ContentTypeBadge = styled.div`
  display: inline-block;
  background: ${props => props.type === 'shop_item' 
    ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' 
    : '#333333'};
  color: ${props => props.type === 'shop_item' ? '#000000' : '#ffffff'};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 8px;
  font-family: var(--font-body);
`;

const ShopItemPrice = styled.div`
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-gold);
  margin-top: 8px;
`;

const PostActions = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: #666666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-body);
  font-size: 0.75rem;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ffffff;
    background: #333333;
  }
  
  &.liked {
    color: #ff6b6b;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SidebarCard = styled.div`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 20px;
`;

const SidebarTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SeeAllLink = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-gold);
  font-family: var(--font-body);
  font-size: 0.75rem;
  cursor: pointer;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ffffff;
  }
`;

const FollowedCreator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1a1a1a;
  }
`;

const CreatorAvatarSmall = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading);
  font-weight: 600;
  color: #000000;
  font-size: 0.75rem;
`;

const CreatorNameSmall = styled.span`
  font-family: var(--font-body);
  color: #cccccc;
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666666;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #333333;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  
  svg {
    width: 40px;
    height: 40px;
    color: #666666;
  }
`;

const EmptyStateTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.25rem;
  color: #cccccc;
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-family: var(--font-body);
  color: #666666;
  font-size: 0.875rem;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #666666;
`;

const FeedPage = () => {
  const { user } = useAuth();
  const [feedData, setFeedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('following');
  const [likedPosts, setLikedPosts] = useState(new Set());

  useEffect(() => {
    if (user) {
      loadFeedData();
    }
  }, [user, activeTab]);

  const loadFeedData = async () => {
    try {
      setLoading(true);
      
      let data;
      if (activeTab === 'following') {
        data = await feedService.getFeed();
      } else {
        data = await feedService.getTrendingPosts();
      }

      setFeedData(data);
    } catch (error) {
      console.error('Error loading feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const isLiked = likedPosts.has(postId);
      
      if (isLiked) {
        await feedService.unlikePost(postId);
      } else {
        await feedService.likePost(postId);
      }

      const newLikedPosts = new Set(likedPosts);
      if (isLiked) {
        newLikedPosts.delete(postId);
      } else {
        newLikedPosts.add(postId);
      }
      setLikedPosts(newLikedPosts);
      loadFeedData(); // Refresh to get updated counts
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
  };

  if (!user) {
    return (
      <FeedContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>Please log in to view your feed</h2>
        </div>
      </FeedContainer>
    );
  }

  return (
    <FeedContainer>
      <FeedLayout>
        <MainFeed>
          <FeedHeader>
            <FeedTitle>Feed</FeedTitle>
            <FeedTabs>
              <TabButton 
                active={activeTab === 'following'} 
                onClick={() => setActiveTab('following')}
              >
                Following
              </TabButton>
              <TabButton 
                active={activeTab === 'trending'} 
                onClick={() => setActiveTab('trending')}
              >
                <TrendingUp size={16} />
                Trending
              </TabButton>
            </FeedTabs>
          </FeedHeader>

          {loading ? (
            <LoadingSpinner>
              <Clock size={24} />
              <span style={{ marginLeft: '8px' }}>Loading feed...</span>
            </LoadingSpinner>
          ) : (
            <>
              {activeTab === 'following' && (!feedData?.posts || feedData.posts.length === 0) ? (
                <EmptyState>
                  <EmptyStateIcon>
                    <Users size={40} />
                  </EmptyStateIcon>
                  <EmptyStateTitle>No posts yet</EmptyStateTitle>
                  <EmptyStateText>
                    Follow some creators to see their posts in your feed
                  </EmptyStateText>
                </EmptyState>
              ) : (
                <>
                  {activeTab === 'following' && feedData?.posts?.map((post) => (
                    <PostCard
                      key={`${post.content_type}-${post.post_id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <PostHeader>
                        <CreatorInfo>
                          <CreatorAvatar>
                            {getInitials(post.creator_name)}
                          </CreatorAvatar>
                          <CreatorDetails>
                            <CreatorName>{post.creator_name}</CreatorName>
                            <PostTime>{formatTimeAgo(post.created_at)}</PostTime>
                          </CreatorDetails>
                        </CreatorInfo>
                        <PostMenu>
                          <MoreHorizontal size={16} />
                        </PostMenu>
                      </PostHeader>

                      <PostContent>
                        <ContentTypeBadge type={post.content_type}>
                          {post.content_type === 'shop_item' ? '🛍️ Shop Item' : '📝 Post'}
                        </ContentTypeBadge>
                        {post.title && <PostTitle>{post.title}</PostTitle>}
                        <PostText>{post.content}</PostText>
                        {post.content_type === 'shop_item' && post.post_type && (
                          <ShopItemPrice>Type: {post.post_type}</ShopItemPrice>
                        )}
                        {post.image_url && (
                          <PostImage>
                            <img 
                              src={getAssetUrl(post.image_url)} 
                              alt="Post content" 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }} 
                            />
                          </PostImage>
                        )}
                      </PostContent>

                      <PostActions>
                        {post.content_type === 'post' && (
                          <>
                            <ActionButton 
                              className={likedPosts.has(post.post_id) ? 'liked' : ''}
                              onClick={() => handleLike(post.post_id)}
                            >
                              <Heart size={16} fill={likedPosts.has(post.post_id) ? 'currentColor' : 'none'} />
                              {post.likes_count || 0}
                            </ActionButton>
                            <ActionButton>
                              <MessageCircle size={16} />
                              {post.comments_count || 0}
                            </ActionButton>
                          </>
                        )}
                        <ActionButton>
                          <Share2 size={16} />
                          Share
                        </ActionButton>
                        {post.content_type === 'shop_item' && (
                          <ActionButton style={{ color: 'var(--primary-gold)' }}>
                            <ShoppingBag size={16} />
                            Buy Now
                          </ActionButton>
                        )}
                      </PostActions>
                    </PostCard>
                  ))}

                  {activeTab === 'trending' && feedData?.trendingPosts?.map((post) => (
                    <PostCard
                      key={post.post_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <PostHeader>
                        <CreatorInfo>
                          <CreatorAvatar>
                            {getInitials(post.creator_name)}
                          </CreatorAvatar>
                          <CreatorDetails>
                            <CreatorName>{post.creator_name}</CreatorName>
                            <PostTime>{formatTimeAgo(post.created_at)}</PostTime>
                          </CreatorDetails>
                        </CreatorInfo>
                        <PostMenu>
                          <MoreHorizontal size={16} />
                        </PostMenu>
                      </PostHeader>

                      <PostContent>
                        {post.title && <PostTitle>{post.title}</PostTitle>}
                        <PostText>{post.content}</PostText>
                        {post.image_url && (
                          <PostImage>
                            <img 
                              src={getAssetUrl(post.image_url)} 
                              alt="Post content" 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }} 
                            />
                          </PostImage>
                        )}
                      </PostContent>

                      <PostActions>
                        <ActionButton 
                          className={likedPosts.has(post.post_id) ? 'liked' : ''}
                          onClick={() => handleLike(post.post_id)}
                        >
                          <Heart size={16} fill={likedPosts.has(post.post_id) ? 'currentColor' : 'none'} />
                          {post.likes_count || 0}
                        </ActionButton>
                        <ActionButton>
                          <MessageCircle size={16} />
                          {post.comments_count || 0}
                        </ActionButton>
                        <ActionButton>
                          <Share2 size={16} />
                          Share
                        </ActionButton>
                      </PostActions>
                    </PostCard>
                  ))}
                </>
              )}
            </>
          )}
        </MainFeed>

        <Sidebar>
          {activeTab === 'following' && feedData?.recentlyFollowed && feedData.recentlyFollowed.length > 0 && (
            <SidebarCard>
              <SidebarTitle>
                <Users size={16} />
                Recently Followed
                <SeeAllLink>
                  See all
                  <ChevronRight size={12} />
                </SeeAllLink>
              </SidebarTitle>
              {feedData.recentlyFollowed.map((creator) => (
                <FollowedCreator key={creator.creator_id}>
                  <CreatorAvatarSmall>
                    {getInitials(creator.display_name)}
                  </CreatorAvatarSmall>
                  <CreatorNameSmall>{creator.display_name}</CreatorNameSmall>
                </FollowedCreator>
              ))}
            </SidebarCard>
          )}

          <SidebarCard>
            <SidebarTitle>
              <TrendingUp size={16} />
              Trending Now
            </SidebarTitle>
            <EmptyStateText style={{ textAlign: 'center', margin: 0 }}>
              Discover popular content from creators
            </EmptyStateText>
          </SidebarCard>
        </Sidebar>
      </FeedLayout>
    </FeedContainer>
  );
};

export default FeedPage;
